import { useRateManagement } from '@api/useFreight/useFreight'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Form, Input, Select, Spin, Switch } from 'antd'
import { useEffect, useState } from 'react'

import type { ProductMinimum, ProductMinimumsProps } from '../../../../types'
import { COMBINED_MINIMUM_METHOD_OPTIONS } from '../constants'

export function ProductMinimums({ ratebook, form, onFormChange }: ProductMinimumsProps) {
  const { useFilteredMetadataQuery } = useRateManagement()
  const { data: metadata, isLoading: isMetadataLoading } = useFilteredMetadataQuery(ratebook)
  const { product_minimums: productGroups = [] } = metadata?.ratebook_options || {}

  const formProductMinimums = Form.useWatch('product_minimums', form)

  const [productMinimums, setProductMinimums] = useState<Record<string, [boolean, string, string]>>({})

  // Initialize from form data
  useEffect(() => {
    if (productGroups.length === 0) return

    const minimums = formProductMinimums || []
    const initialState: Record<string, [boolean, string, string]> = {}

    for (const group of productGroups) {
      const minimum = minimums.find((min: ProductMinimum) => min.product_group === group)
      initialState[group] = [
        !!minimum,
        minimum ? minimum.minimum_volume.toString() : '',
        minimum?.minimum_method || 'Highest Rate',
      ]
    }

    setProductMinimums(initialState)
  }, [productGroups, formProductMinimums])

  // Handle toggle switches
  const handleToggle = (product: string, enabled: boolean) => {
    setProductMinimums((prev) => {
      const newState: Record<string, [boolean, string, string]> = {
        ...prev,
        [product]: [enabled, enabled ? prev[product]?.[1] || '0' : '', prev[product]?.[2] || 'Highest Rate'],
      }

      updateFormMinimums(newState)
      return newState
    })
  }

  // Handle value changes - only allow numbers
  const handleValueChange = (product: string, value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '')

    setProductMinimums((prev) => {
      const newState: Record<string, [boolean, string, string]> = {
        ...prev,
        [product]: [prev[product]?.[0] || false, numericValue, prev[product]?.[2] || 'Highest Rate'],
      }

      updateFormMinimums(newState)
      return newState
    })
  }

  // Handle method changes for Gas and Diesel
  const handleMethodChange = (product: string, method: string) => {
    setProductMinimums((prev) => {
      const newState: Record<string, [boolean, string, string]> = {
        ...prev,
        [product]: [prev[product]?.[0] || false, prev[product]?.[1] || '', method],
      }

      updateFormMinimums(newState)
      return newState
    })
  }

  // Update form data with minimums
  const updateFormMinimums = (minimums: Record<string, [boolean, string, string]>) => {
    const formMinimums: ProductMinimum[] = []

    for (const [group, [enabled, value, method]] of Object.entries(minimums)) {
      if (enabled && value) {
        const minimum: ProductMinimum = {
          product_group: group,
          minimum_volume: Number.parseInt(value, 10) || 0,
        }

        // Only add minimum_method for Gas and Diesel
        if (group === 'Gas and Diesel') {
          minimum.minimum_method = method
        }

        formMinimums.push(minimum)
      }
    }

    form.setFieldValue('product_minimums', formMinimums)

    onFormChange()
  }

  return (
    <Vertical style={{ maxWidth: 600 }}>
      <Vertical border='top' className='py-3'>
        <Texto category='h5' weight='600' className='mt-3'>
          Product Minimum Configuration
        </Texto>
      </Vertical>

      <Vertical bg='elevated' border='all' borderRadius='4px' className='py-1 px-3'>
        <Form.Item name='product_minimums' noStyle>
          <Vertical style={{ minHeight: 200, overflowY: 'auto' }} className='px-2' gap={8}>
            {(() => {
              if (isMetadataLoading) {
                return (
                  <Vertical verticalCenter horizontalCenter>
                    <Spin spinning size='small' />
                  </Vertical>
                )
              }

              if (productGroups.length === 0) {
                return <Texto>No product groups available</Texto>
              }

              return productGroups.map((product, index) => (
                <Vertical
                  key={product}
                  className='py-2'
                  border={index < productGroups.length - 1 ? 'bottom' : undefined}
                  gap={8}
                >
                  <Horizontal gap={16} verticalCenter>
                    <Texto style={{ flex: 1 }}>{product}</Texto>
                    <Switch
                      checked={productMinimums[product]?.[0] || false}
                      onChange={(checked) => handleToggle(product, checked)}
                    />
                    <Input
                      value={productMinimums[product]?.[1] || ''}
                      onChange={(e) => handleValueChange(product, e.target.value)}
                      disabled={!productMinimums[product]?.[0]}
                      style={{ width: 100 }}
                    />
                    <Texto>gallons</Texto>
                  </Horizontal>
                  {product === 'Gas and Diesel' && productMinimums[product]?.[0] && (
                    <Horizontal gap={16} verticalCenter className='pl-4'>
                      <Texto style={{ flex: 1 }}>Method:</Texto>
                      <Select
                        value={productMinimums[product]?.[2] || 'Highest Rate'}
                        onChange={(value) => handleMethodChange(product, value)}
                        style={{ width: 150 }}
                        options={COMBINED_MINIMUM_METHOD_OPTIONS}
                      />
                    </Horizontal>
                  )}
                </Vertical>
              ))
            })()}
          </Vertical>
        </Form.Item>
      </Vertical>
    </Vertical>
  )
}
